import { useEffect, useState } from 'react'
import { isSubscriptionActive, type UsageSnapshot } from '@/lib/baba-api'
import { GUEST_CHARS_PER_MONTH, FREE_CHARS_PER_MONTH } from '@/lib/baba-limits'
import { isSignedIn } from '@/lib/baba-auth'
import { badgeProgress } from '@/lib/progress/badges'
import { useProgress } from '../hooks/useProgress'
import { ensureGatewaySourceProbe, useGatewaySourceStatus } from '@/lib/library/useLibrarySource'
import { babaApi, useSubscriptionStatusQuery, useUsageQuery } from '../store/babaApi'
import { useAppDispatch } from '../store/hooks'
import { usePaywall } from './paywall'
import { useSignIn } from './SignInModal'

function readUsageBootstrap(): UsageSnapshot | null {
  try {
    const stored = localStorage.getItem('baba.app.last_usage')
    return stored ? (JSON.parse(stored) as UsageSnapshot) : null
  } catch {
    return null
  }
}

/**
 * Remaining-limits data for non-Pro users (guests included) — shared by the
 * plan summary and the mobile conversion strip so both read the same numbers.
 * Same-tab updates arrive via the custom event the translator fires after
 * every run; cross-tab updates land through localStorage and refresh the RTK
 * cache for signed-in users.
 */
export function useUsageSnapshot(signedIn: boolean) {
  const dispatch = useAppDispatch()
  // Once this session's text translations are confirmed running on the
  // gateway ledger (see translateGateway/client.ts and the shared probe in
  // lib/library/useLibrarySource.ts), apps/api's OWN usage numbers are for a
  // ledger this user no longer writes to — reading them here would show a
  // stale, misleadingly generous remaining count. Skip that query and rely
  // on the `baba:usage-updated` snapshot the gateway path publishes after
  // each translation (see translationUsage.ts) instead. Until the routing
  // check has answered, the meter keeps the last number it showed: reading
  // apps/api in that gap made it step through three values on every load.
  const routing = useGatewaySourceStatus()
  const isGatewaySource = routing === 'gateway'
  useEffect(() => {
    if (signedIn) void ensureGatewaySourceProbe()
  }, [signedIn])
  const { data: usageFromApi } = useUsageQuery(undefined, { skip: !signedIn || routing !== 'fallback' })
  const [snapshot, setSnapshot] = useState<UsageSnapshot | null>(() => readUsageBootstrap())
  // On load (before any translation this session) the gateway ledger is read
  // once from `GET /me`, so the meter shows the phone's real remaining count
  // instead of an empty or last-session number. The /me answer is shared
  // (fetchGatewayMe), so re-mounting this card on every rail expand no
  // longer asks again.
  useEffect(() => {
    if (!signedIn || !isGatewaySource) return
    let cancelled = false
    void (async () => {
      try {
        const [{ fetchGatewayMe }, { usageSnapshotFromGatewayMe }] = await Promise.all([
          import('@/lib/gateway'),
          import('@/lib/translateGateway/usageFromMe'),
        ])
        const me = await fetchGatewayMe()
        const next = usageSnapshotFromGatewayMe(me as Parameters<typeof usageSnapshotFromGatewayMe>[0])
        if (cancelled || !next) return
        setSnapshot(next)
        try {
          localStorage.setItem('baba.app.last_usage', JSON.stringify(next))
        } catch {}
      } catch {
        // Silent: the meter keeps its bootstrap value and updates after the next translation.
      }
    })()
    return () => {
      cancelled = true
    }
  }, [signedIn, isGatewaySource])
  useEffect(() => {
    const onUsage = (e: Event) => {
      const detail = (e as CustomEvent<UsageSnapshot>).detail
      if (detail) setSnapshot(detail)
    }
    window.addEventListener('baba:usage-updated', onUsage as EventListener)
    return () => window.removeEventListener('baba:usage-updated', onUsage as EventListener)
  }, [])
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'baba.app.last_usage' && e.newValue) {
        try {
          const parsed = JSON.parse(e.newValue) as UsageSnapshot
          if (signedIn) dispatch(babaApi.util.upsertQueryData('usage', undefined, parsed))
        } catch {}
      }
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [dispatch, signedIn])
  const usage = routing === 'fallback' ? (usageFromApi ?? snapshot) : snapshot
  const limit = usage?.charsLimit ?? (signedIn ? FREE_CHARS_PER_MONTH : GUEST_CHARS_PER_MONTH)
  const used = usage?.charsUsed ?? 0
  const left = Math.max(0, limit - used)
  const pct = limit > 0 ? Math.min(100, (used / limit) * 100) : 0
  return { limit, used, left, pct, atLimit: left === 0 }
}

function TierChip({ tier }: { tier: string }) {
  return (
    <span
      // Deliberately neutral, not terracotta: this is a status label, and the
      // accent belongs to the primary action and the current selection.
      className="shrink-0 rounded font-mono uppercase tracking-wider"
      style={{ fontSize: 9, lineHeight: '14px', padding: '0 5px', border: '1px solid var(--line)', background: 'var(--card-2)', color: 'var(--muted)' }}
    >
      {tier}
    </span>
  )
}

/**
 * Plan + usage summary. One component, four places: the bordered card the
 * Dictionary panel still renders (`panel`), the card in the expanded rail
 * (`rail`), the borderless block inside the rail's account popover (`menu`)
 * and the single row above the email in the mobile More sheet (`sheet`). It
 * moved out of the History and Chat panels so the plan reads next to the
 * account it belongs to, once, instead of at the bottom of every page.
 */
export function PlanSummary({ variant }: { variant: 'panel' | 'rail' | 'menu' | 'sheet' }) {
  const { open: openPaywall } = usePaywall()
  const { open: openSignIn } = useSignIn()
  const signedIn = isSignedIn()
  const { data: subscription } = useSubscriptionStatusQuery(undefined, { skip: !signedIn })
  const isPro = subscription ? isSubscriptionActive(subscription) : false
  const isProPlus = isPro && subscription?.tier === 'pro_plus'
  const { limit, left, pct, atLimit } = useUsageSnapshot(signedIn)
  const { totalTranslations } = useProgress()
  const badges = badgeProgress(totalTranslations)

  // The wordmark stays one short word and the tier lives in the chip beside it.
  const planName = isPro ? 'baba' : signedIn ? 'Free Plan' : 'Guest'
  const tierChip = isProPlus ? 'PRO+' : isPro ? 'PRO' : null
  // Neutral fill by default — terracotta is reserved for the primary action and
  // the current selection; the bar only turns colour when it needs attention.
  const barColor = atLimit ? 'var(--err)' : pct >= 90 ? 'var(--warn)' : 'var(--muted-2)'
  const counter = `${left.toLocaleString()} / ${limit.toLocaleString()}`
  const bodyText = variant === 'rail' ? 'text-[12px]' : 'text-[12.5px]'

  const nameRow = (
    <span className="flex min-w-0 items-center gap-1.5">
      <span className={`${bodyText} truncate font-semibold text-app`}>{planName}</span>
      {tierChip && <TierChip tier={tierChip} />}
    </span>
  )

  if (variant === 'sheet') {
    return (
      <div className="flex items-center justify-between gap-2 px-3 py-1.5">
        {nameRow}
        {!isPro && (
          <span className="shrink-0 font-mono text-[11px]" style={{ color: 'var(--muted-2)' }}>
            {counter}
          </span>
        )}
      </div>
    )
  }

  const cardClass =
    variant === 'menu'
      ? 'flex flex-col gap-1.5 px-[14px] pt-2 pb-2.5'
      : `flex w-full flex-col gap-1.5 rounded-md ${variant === 'rail' ? 'px-[11px] py-2.5' : 'p-2.5'}`

  return (
    <div className={cardClass} style={variant === 'menu' ? undefined : { background: 'var(--card)', border: '1px solid var(--line)' }}>
      <div className="flex items-center justify-between gap-2">
        {nameRow}
        {!isPro && (
          <span className="shrink-0 font-mono text-[11px]" style={{ color: 'var(--muted-2)' }}>
            {counter}
          </span>
        )}
      </div>

      {!isPro && (
        <div className="h-[3px] overflow-hidden rounded-full" style={{ background: 'var(--card-2)' }}>
          <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: barColor }} />
        </div>
      )}

      {/* How far the next badge is, for anyone signed in — the Badges page
        * was the only place that knew, and it is three clicks away. A Pro
        * card has no allowance bar, so this is what fills that line. */}
      {signedIn && badges.next && variant !== 'menu' && (
        <>
          <div className="flex items-center justify-between gap-2">
            <span className={`${bodyText} truncate`} style={{ color: 'var(--muted)' }}>
              {badges.next.name}
            </span>
            <span className="shrink-0 font-mono text-[11px]" style={{ color: 'var(--muted-2)' }}>
              {badges.remaining.toLocaleString()} to go
            </span>
          </div>
          <div className="h-[3px] overflow-hidden rounded-full" style={{ background: 'var(--card-2)' }}>
            <div
              className="h-full rounded-full transition-all"
              style={{ width: `${badges.pct}%`, background: 'var(--accent)' }}
            />
          </div>
        </>
      )}

      {!signedIn && (
        <button
          type="button"
          onClick={() => openSignIn('sidebar')}
          className="touch-44-y text-left text-[12px] font-semibold hover:underline transition-colors duration-150 cursor-pointer"
          style={{ color: 'var(--accent-deep)' }}
        >
          Sign In Free →
        </button>
      )}
      {signedIn && !isPro && (
        <button
          type="button"
          onClick={() => openPaywall('upgrade')}
          className="touch-44-y text-left text-[12px] font-semibold hover:underline transition-colors duration-150 cursor-pointer"
          style={{ color: 'var(--accent-deep)' }}
        >
          Upgrade to Pro →
        </button>
      )}
      {isPro && (
        // The "same email" instruction people actually need is on the tooltip
        // rather than wrapping the card.
        <span
          className="text-[11.5px]"
          title="Sign in on the app with the same email and your plan carries over."
          style={{ color: 'var(--muted-2)' }}
        >
          Also on iPhone and Android.
        </span>
      )}
    </div>
  )
}
